// @ts-strict-ignore
import { ListFilters } from "@dashboard/components/AppLayout/ListFilters";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ListPageLayout } from "@dashboard/components/Layouts";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import { useFilterHandlers } from "@dashboard/hooks/useFilterHandlers";
import useListSettings from "@dashboard/hooks/useListSettings";
import useNavigator from "@dashboard/hooks/useNavigator";
import { usePaginationReset } from "@dashboard/hooks/usePaginationReset";
import { PaginatorContext } from "@dashboard/hooks/usePaginator";
import { commonMessages, sectionNames } from "@dashboard/intl";
import { OrderSubscriptionsListDatagrid } from "@dashboard/orders/components/OrderSubscriptionsListDatagrid";
import {
  orderSubscriptionDetailPath,
  orderSubscriptionsListUrl,
  SubscriptionListUrlQueryParams,
  SubscriptionListUrlSortField,
} from "@dashboard/orders/urls";
import { ListViews } from "@dashboard/types";
import createSortHandler from "@dashboard/utils/handlers/sortHandler";
import { getSortParams } from "@dashboard/utils/sort";
import { stringifyQs } from "@dashboard/utils/urls";
import { Card, CircularProgress } from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import { Box, ChevronRightIcon, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { fetchSubscriptions } from "../OrderSubscriptions/api";
import {
  createFilterStructure,
  getFilterOpts,
  getFilterQueryParam,
} from "./filters";
import {
  filterSubscriptionRows,
  sortSubscriptionRows,
  SubscriptionRow,
} from "./OrderSubscriptionsList.utils";

const useStyles = makeStyles(
  theme => {
    return {
      loader: {
        display: "flex",
        justifyContent: "center",
        padding: theme.spacing(9),
      },
    };
  },
  { name: "OrderSubscriptionsList" },
);

export interface OrderSubscriptionsListProps {
  params: SubscriptionListUrlQueryParams;
}

const OrderSubscriptionsList: React.FC<OrderSubscriptionsListProps> = ({
  params,
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const navigate = useNavigator();
  const { updateListSettings, settings } = useListSettings(
    ListViews.SUBSCRIPTION_LIST,
  );

  const [allRows, setAllRows] = React.useState<SubscriptionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  usePaginationReset(orderSubscriptionsListUrl, params, settings.rowNumber);

  const filterOpts = React.useMemo(() => getFilterOpts(params), [params]);
  const filterStructure = React.useMemo(
    () => createFilterStructure(intl, filterOpts),
    [intl, filterOpts],
  );

  const [changeFilters, , handleSearchChange] = useFilterHandlers({
    createUrl: orderSubscriptionsListUrl,
    getFilterQueryParam,
    params,
    defaultSortField: SubscriptionListUrlSortField.nextPayment,
  });

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const rows = await fetchSubscriptions();
        if (!cancelled) {
          setAllRows(rows);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Unknown error");
          setAllRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const filteredSorted = React.useMemo(() => {
    const filtered = filterSubscriptionRows(
      allRows,
      params.query,
      params.status,
    );
    return sortSubscriptionRows(
      filtered,
      params.sort ?? SubscriptionListUrlSortField.nextPayment,
      params.asc,
    );
  }, [allRows, params.query, params.status, params.sort, params.asc]);

  const pageSize = settings.rowNumber;
  const rawOffset = params.after ? parseInt(params.after, 10) || 0 : 0;
  const offset = React.useMemo(() => {
    if (!filteredSorted.length) {
      return 0;
    }
    const lastValidStart =
      Math.floor((filteredSorted.length - 1) / pageSize) * pageSize;
    return Math.min(Math.max(0, rawOffset), lastValidStart);
  }, [filteredSorted.length, pageSize, rawOffset]);

  const pageRows = filteredSorted.slice(offset, offset + pageSize);

  const hasNextPage = offset + pageSize < filteredSorted.length;
  const hasPreviousPage = offset > 0;

  const nextHref = hasNextPage
    ? `?${stringifyQs({
        ...params,
        after: String(offset + pageSize),
        before: undefined,
      })}`
    : undefined;
  const prevHref = hasPreviousPage
    ? `?${stringifyQs({
        ...params,
        after: offset - pageSize <= 0 ? undefined : String(offset - pageSize),
        before: undefined,
      })}`
    : undefined;

  const paginationContextValue = React.useMemo(
    () => ({
      paginatorType: "link" as const,
      nextHref,
      prevHref,
      hasNextPage,
      hasPreviousPage,
    }),
    [hasNextPage, hasPreviousPage, nextHref, prevHref],
  );

  const sort = getSortParams(params) as Partial<{
    asc: boolean;
    sort: SubscriptionListUrlSortField;
  }>;
  const handleSort = createSortHandler(
    navigate,
    orderSubscriptionsListUrl,
    params,
  );

  return (
    <ListPageLayout>
      <WindowTitle title={intl.formatMessage(commonMessages.subscriptions)} />
      <TopNav
        title={intl.formatMessage(sectionNames.orders)}
        withoutBorder
        isAlignToRight={false}
      >
        <Box
          __flex={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" alignItems="center">
            <Box marginX={3} display="flex" alignItems="center">
              <ChevronRightIcon />
            </Box>
            <Text
              variant="title"
              size="small"
              data-test-id="subscriptions-breadcrumb"
            >
              <FormattedMessage
                id="8yyU6n"
                defaultMessage="All subscriptions"
              />
            </Text>
          </Box>
        </Box>
      </TopNav>

      <Card>
        <ListFilters
          initialSearch={params.query || ""}
          onFilterChange={changeFilters}
          onSearchChange={handleSearchChange}
          filterStructure={filterStructure}
          searchPlaceholder={intl.formatMessage({
            id: "8VF7b8",
            defaultMessage: "Search subscriptions…",
          })}
        />

        {loading ? (
          <Box className={classes.loader} data-test-id="subscriptions-loader">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box padding={6}>{error}</Box>
        ) : (
          <PaginatorContext.Provider value={paginationContextValue}>
            <OrderSubscriptionsListDatagrid
              rows={pageRows}
              disabled={loading}
              settings={settings}
              onUpdateListSettings={updateListSettings}
              sort={sort}
              onSort={handleSort}
              rowAnchor={orderSubscriptionDetailPath}
            />
          </PaginatorContext.Provider>
        )}
      </Card>
    </ListPageLayout>
  );
};

export default OrderSubscriptionsList;
